import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { Business, BusinessContextType } from '../types/business';
import { useAuth } from './AuthContext';
import { apiService } from '../services/api';

const SalonContext = createContext<BusinessContextType | undefined>(undefined);

export const SalonProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const { user } = useAuth();
    const [businesses, setBusinesses] = useState<Business[]>([]);
    const [currentBusiness, setCurrentBusinessState] = useState<Business | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Fetch businesses when user logs in
    useEffect(() => {
        if (user) {
            fetchBusinesses();
        } else {
            setBusinesses([]);
            setCurrentBusinessState(null);
            setLoading(false);
        }
    }, [user]);

    const fetchBusinesses = async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await apiService.getBusinesses();

            if (response.success && response.data) {
                let businessList: Business[] = response.data;

                // Auto-fix businesses missing bookingCode by calling migrate endpoint
                const missingCode = businessList.filter((b: Business) => !b.bookingCode);
                if (missingCode.length > 0) {
                    try {
                        // Call migration endpoint to generate codes for existing businesses
                        await fetch(
                            `${(apiService as any).getBaseUrl?.() || 'https://backend-sable-five-58.vercel.app/api'}/public/migrate-codes`,
                            {
                                method: 'POST',
                                headers: {
                                    'Content-Type': 'application/json',
                                    'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
                                }
                            }
                        );
                        // Refetch to get updated booking codes
                        const refreshed = await apiService.getBusinesses();
                        if (refreshed.success && refreshed.data) {
                            businessList = refreshed.data;
                        }
                    } catch (migrateErr) {
                        console.warn('Could not auto-migrate booking codes:', migrateErr);
                    }
                }

                setBusinesses(businessList);

                // Set current business from localStorage or default to first
                const savedBusinessId = localStorage.getItem('currentBusinessId');
                if (savedBusinessId) {
                    const savedBusiness = businessList.find((b: Business) => b.id === savedBusinessId);
                    if (savedBusiness) {
                        setCurrentBusinessState(savedBusiness);
                    } else if (businessList.length > 0) {
                        setCurrentBusinessState(businessList[0]);
                        localStorage.setItem('currentBusinessId', businessList[0].id);
                    }
                } else if (businessList.length > 0) {
                    setCurrentBusinessState(businessList[0]);
                    localStorage.setItem('currentBusinessId', businessList[0].id);
                }
            }
        } catch (err: any) {
            console.error('Failed to fetch businesses:', err);
            setError(err.message || 'Failed to load businesses');
        } finally {
            setLoading(false);
        }
    };

    const switchBusiness = (businessId: string) => {
        const business = businesses.find(b => b.id === businessId);
        if (business) {
            setCurrentBusinessState(business);
            localStorage.setItem('currentBusinessId', businessId);
            // Redirect to dashboard to refresh data in the new business context
            window.location.href = '/dashboard';
        }
    };

    const createBusiness = async (data: Partial<Business>): Promise<Business> => {
        try {
            setError(null);
            const response = await apiService.createBusiness(data);

            if (response.success && response.data) {
                const newBusiness = response.data;
                setBusinesses(prev => [...prev, newBusiness]);
                // Auto-switch to new business
                setCurrentBusinessState(newBusiness);
                localStorage.setItem('currentBusinessId', newBusiness.id);
                return newBusiness;
            }
            throw new Error('Failed to create business');
        } catch (err: any) {
            setError(err.message || 'Failed to create business');
            throw err;
        }
    };

    const updateBusiness = async (id: string, data: Partial<Business>): Promise<Business> => {
        try {
            setError(null);
            const response = await apiService.updateBusiness(id, data);

            if (response.success && response.data) {
                console.log('[SalonContext] updateBusiness API response data:', response.data);
                const updatedBusiness = response.data;
                setBusinesses(prev =>
                    prev.map(b => b.id === id ? updatedBusiness : b)
                );
                if (currentBusiness?.id === id) {
                    setCurrentBusinessState(updatedBusiness);
                }
                return updatedBusiness;
            }
            throw new Error('Failed to update business');
        } catch (err: any) {
            setError(err.message || 'Failed to update business');
            throw err;
        }
    };

    return (
        <SalonContext.Provider value={{
            businesses,
            currentBusiness,
            switchBusiness,
            createBusiness,
            updateBusiness,
            loading,
            error
        }}>
            {children}
        </SalonContext.Provider>
    );
};

export const useSalon = () => {
    const context = useContext(SalonContext);
    if (context === undefined) {
        throw new Error('useSalon must be used within a SalonProvider');
    }
    return context;
};
